import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Badge, Pagination, Button, Row, Col, Alert, Modal, InputGroup } from 'react-bootstrap';
import api from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { showToast } from '../../hooks/useToast';

const EmailQueue = () => {
  const { selectedTenant } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [tableExists, setTableExists] = useState(true);
  const [counts, setCounts] = useState({ total: 0, pending: 0, processing: 0, failed: 0 });

  // Modal State for viewing email details / errors
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchQueue = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      };
      const { data } = await api.get('/emails/queue', { params });
      setTableExists(data.tableExists);
      setItems(data.items || []);
      setCounts(data.counts || { total: 0, pending: 0, processing: 0, failed: 0, sent: 0 });
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
    } catch (error) {
      console.error('Error fetching email queue:', error);
      showToast('Error al consultar la cola de correos', 'danger');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, pagination.page, pagination.pageSize, filters.status, filters.search]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Auto-refresh every 10 seconds if there are pending/processing items
  useEffect(() => {
    let timer = null;
    if (counts.pending > 0 || counts.processing > 0) {
      timer = setInterval(() => {
        fetchQueue();
      }, 10000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [counts.pending, counts.processing, fetchQueue]);

  const handleProcessNow = async () => {
    setActionLoading(true);
    try {
      await api.post('/emails/process-now');
      showToast('Procesamiento de cola de correos iniciado', 'success');
      setTimeout(fetchQueue, 1000);
    } catch (error) {
      console.error('Error processing email queue:', error);
      showToast('Error al iniciar el procesamiento', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryOne = async (id) => {
    try {
      await api.post(`/emails/retry/${id}`);
      showToast('Correo reprogramado para reenvío', 'success');
      fetchQueue();
      if (showModal) setShowModal(false);
    } catch (error) {
      console.error('Error retrying email:', error);
      showToast('Error al reprogramar el correo', 'danger');
    }
  };

  const handleRetryAll = async () => {
    if (!window.confirm('¿Deseas reprogramar todos los correos fallidos para su reenvío?')) return;
    setActionLoading(true);
    try {
      const { data } = await api.post('/emails/retry-all');
      showToast(data.message || 'Correos reprogramados con éxito', 'success');
      fetchQueue();
    } catch (error) {
      console.error('Error retrying all emails:', error);
      showToast('Error al reprogramar correos fallidos', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteOne = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este correo de la cola?')) return;
    try {
      await api.delete(`/emails/item/${id}`);
      showToast('Correo eliminado de la cola', 'info');
      fetchQueue();
      if (showModal) setShowModal(false);
    } catch (error) {
      console.error('Error deleting email:', error);
      showToast('Error al eliminar correo', 'danger');
    }
  };

  const handleClearFailed = async () => {
    if (!window.confirm('¿Seguro que deseas eliminar todos los correos con estado fallido?')) return;
    setActionLoading(true);
    try {
      const { data } = await api.delete('/emails/clear-failed');
      showToast(data.message || 'Correos fallidos eliminados', 'info');
      fetchQueue();
    } catch (error) {
      console.error('Error clearing failed emails:', error);
      showToast('Error al eliminar correos fallidos', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (email) => {
    setSelectedEmail(email);
    setShowModal(true);
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={pagination.page === 1}
        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
      />
    );

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === pagination.page}
          onClick={() => setPagination(prev => ({ ...prev, page: i }))}
        >
          {i}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={pagination.page === totalPages}
        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
      />
    );

    return <Pagination className="mb-0 justify-content-center">{items}</Pagination>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning" className="text-dark"><i className="bi bi-clock me-1"></i>Pendiente</Badge>;
      case 'processing':
        return <Badge bg="info"><i className="bi bi-arrow-repeat me-1"></i>Enviando</Badge>;
      case 'sent':
        return <Badge bg="success"><i className="bi bi-check-circle me-1"></i>Enviado</Badge>;
      case 'failed':
        return <Badge bg="danger"><i className="bi bi-x-circle me-1"></i>Fallido</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-envelope-paper text-primary me-2"></i>
            Gestor de Envíos de Correos
          </h2>
          <p className="text-muted mb-0 small">
            Supervisa el estado de la cola de correos electrónicos, reintentos y errores de entrega (Amazon SES).
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={fetchQueue}
            disabled={loading}
            title="Actualizar lista"
          >
            <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'spin' : ''}`}></i>
            Actualizar
          </Button>
          {counts.failed > 0 && (
            <Button
              variant="outline-danger"
              onClick={handleRetryAll}
              disabled={actionLoading}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              Reintentar Fallidos ({counts.failed})
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleProcessNow}
            disabled={actionLoading || counts.pending === 0}
            className="font-weight-bold"
          >
            {actionLoading ? (
              <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Procesando...</span>
            ) : (
              <span><i className="bi bi-send-check me-1"></i> Procesar Cola Ahora</span>
            )}
          </Button>
        </div>
      </div>

      {/* Alerta de tabla no existente */}
      {!tableExists && (
        <Alert variant="warning" className="mb-4 d-flex align-items-start">
          <i className="bi bi-exclamation-triangle-fill fs-4 me-3 text-warning"></i>
          <div>
            <strong className="d-block">Tabla `email_queue` no encontrada en este colegio</strong>
            <p className="mb-2 small">
              La tabla para almacenar la cola de correos aún no ha sido creada en la base de datos de esta institución.
            </p>
            <details className="mt-1 small">
              <summary className="text-primary cursor-pointer">Ver sentencia SQL para crear la tabla</summary>
              <pre className="bg-dark text-light p-2 mt-2 rounded small">
{`CREATE TABLE IF NOT EXISTS email_queue (
    id SERIAL PRIMARY KEY,
    recipient_email VARCHAR(255) NOT NULL,
    sender_name VARCHAR(255) NOT NULL,
    subject VARCHAR(255) NOT NULL,
    body_text TEXT,
    body_html TEXT,
    status VARCHAR(20) DEFAULT 'pending',
    attempts INT DEFAULT 0,
    max_attempts INT DEFAULT 3,
    error_message TEXT,
    created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
    updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);
CREATE INDEX IF NOT EXISTS idx_email_queue_status ON email_queue(status);`}
              </pre>
            </details>
          </div>
        </Alert>
      )}

      {/* Cards de Métricas / Estado */}
      <Row className="mb-4 g-3">
        <Col xs={12} sm={6} md={4} lg className="col-xl">
          <Card className="shadow-sm border-0 border-start border-4 border-primary h-100">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase font-weight-bold">Total en Cola</div>
                <h3 className="mb-0 mt-1 font-weight-bold text-dark">{counts.total}</h3>
              </div>
              <div className="bg-primary bg-opacity-10 p-3 rounded-circle text-primary">
                <i className="bi bi-inbox fs-4"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={4} lg className="col-xl">
          <Card className="shadow-sm border-0 border-start border-4 border-warning h-100">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase font-weight-bold">Pendientes</div>
                <h3 className="mb-0 mt-1 font-weight-bold text-warning">{counts.pending}</h3>
              </div>
              <div className="bg-warning bg-opacity-10 p-3 rounded-circle text-warning">
                <i className="bi bi-hourglass-split fs-4"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={4} lg className="col-xl">
          <Card className="shadow-sm border-0 border-start border-4 border-info h-100">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase font-weight-bold">Enviando</div>
                <h3 className="mb-0 mt-1 font-weight-bold text-info">{counts.processing}</h3>
              </div>
              <div className="bg-info bg-opacity-10 p-3 rounded-circle text-info">
                <i className="bi bi-arrow-repeat fs-4"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={4} lg className="col-xl">
          <Card className="shadow-sm border-0 border-start border-4 border-success h-100">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase font-weight-bold">Enviados (3 días)</div>
                <h3 className="mb-0 mt-1 font-weight-bold text-success">{counts.sent || 0}</h3>
              </div>
              <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                <i className="bi bi-check-circle fs-4"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={4} lg className="col-xl">
          <Card className="shadow-sm border-0 border-start border-4 border-danger h-100">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase font-weight-bold">Fallidos</div>
                <h3 className="mb-0 mt-1 font-weight-bold text-danger">{counts.failed}</h3>
              </div>
              <div className="bg-danger bg-opacity-10 p-3 rounded-circle text-danger">
                <i className="bi bi-exclamation-triangle fs-4"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros y Tabla */}
      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light py-3">
          <Row className="align-items-center g-2">
            <Col md={6}>
              <InputGroup size="sm">
                <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por destinatario, asunto o remitente..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">Todos los Estados</option>
                <option value="pending">Pendientes</option>
                <option value="processing">En Proceso</option>
                <option value="sent">Enviados (últimos 3 días)</option>
                <option value="failed">Fallidos (con error)</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-md-end">
              {counts.failed > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  className="text-danger p-0 text-decoration-none"
                  onClick={handleClearFailed}
                >
                  <i className="bi bi-trash me-1"></i> Limpiar Fallidos
                </Button>
              )}
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="text-muted mt-2 small">Cargando cola de correos...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-envelope-check text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">Cola de correos limpia</h5>
              <p className="text-muted small">
                {filters.status || filters.search
                  ? 'No se encontraron correos con los filtros seleccionados'
                  : 'No hay correos pendientes ni errores registrados en la base de datos'}
              </p>
            </div>
          ) : (
            <>
              <Table hover responsive className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Destinatario</th>
                    <th>Remitente</th>
                    <th>Asunto</th>
                    <th>Estado</th>
                    <th>Intentos</th>
                    <th>Error / Detalle</th>
                    <th>Fecha</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} className={item.status === 'failed' ? 'table-danger bg-opacity-25' : ''}>
                      <td>
                        <div className="font-weight-bold text-dark">{item.recipient_email}</div>
                      </td>
                      <td>
                        <span className="small text-muted">{item.sender_name || 'Sistema'}</span>
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '220px' }} title={item.subject}>
                          {item.subject}
                        </div>
                      </td>
                      <td>{getStatusBadge(item.status)}</td>
                      <td>
                        <span className={`small ${item.attempts >= item.max_attempts ? 'text-danger font-weight-bold' : 'text-muted'}`}>
                          {item.attempts} / {item.max_attempts || 3}
                        </span>
                      </td>
                      <td>
                        {item.error_message ? (
                          <div 
                            className="text-danger small text-truncate cursor-pointer d-flex align-items-center" 
                            style={{ maxWidth: '200px' }}
                            onClick={() => handleViewDetails(item)}
                            title="Haz clic para ver el error completo"
                          >
                            <i className="bi bi-exclamation-circle-fill me-1 flex-shrink-0"></i>
                            <span className="text-truncate">{item.error_message}</span>
                          </div>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                      <td className="small text-muted text-nowrap">
                        {new Date(item.created_at).toLocaleString('es-MX', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="text-end text-nowrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1 py-0 px-2"
                          onClick={() => handleViewDetails(item)}
                          title="Ver contenido y detalles"
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                        {item.status === 'failed' && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1 py-0 px-2"
                            onClick={() => handleRetryOne(item.id)}
                            title="Reprogramar reintento"
                          >
                            <i className="bi bi-arrow-counterclockwise"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="py-0 px-2"
                          onClick={() => handleDeleteOne(item.id)}
                          title="Eliminar de la cola"
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div className="p-3 border-top">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Detalle de Correo y Error */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className={selectedEmail?.status === 'failed' ? 'bg-danger text-white' : 'bg-light'}>
          <Modal.Title className="fs-5">
            <i className="bi bi-envelope me-2"></i>
            Detalle del Correo #{selectedEmail?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmail && (
            <div>
              {/* Alerta de Error si existe */}
              {selectedEmail.error_message && (
                <Alert variant="danger" className="mb-3">
                  <div className="d-flex align-items-start">
                    <i className="bi bi-exclamation-octagon-fill fs-4 me-3 mt-1"></i>
                    <div>
                      <strong className="d-block mb-1">Error devuelto por el servidor de correo:</strong>
                      <div className="font-monospace small bg-white p-2 rounded border border-danger text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedEmail.error_message}
                      </div>
                    </div>
                  </div>
                </Alert>
              )}

              <Row className="mb-3 g-2">
                <Col sm={6}>
                  <div className="text-muted small">Destinatario:</div>
                  <div className="font-weight-bold">{selectedEmail.recipient_email}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Remitente:</div>
                  <div className="font-weight-bold">{selectedEmail.sender_name}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Estado:</div>
                  <div>{getStatusBadge(selectedEmail.status)}</div>
                </Col>
                <Col sm={6}>
                  <div className="text-muted small">Intentos de envío:</div>
                  <div>{selectedEmail.attempts} de {selectedEmail.max_attempts || 3}</div>
                </Col>
                <Col sm={12}>
                  <div className="text-muted small">Asunto:</div>
                  <div className="font-weight-bold text-dark">{selectedEmail.subject}</div>
                </Col>
              </Row>

              <hr />

              <div className="mb-2 font-weight-bold small text-muted text-uppercase">Cuerpo del Mensaje:</div>
              {selectedEmail.body_html ? (
                <div 
                  className="p-3 bg-light rounded border"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }}
                />
              ) : (
                <pre className="p-3 bg-light rounded border small" style={{ maxHeight: '300px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                  {selectedEmail.body_text || '(Sin contenido de texto)'}
                </pre>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedEmail?.status === 'failed' && (
            <Button variant="success" onClick={() => handleRetryOne(selectedEmail.id)}>
              <i className="bi bi-arrow-counterclockwise me-1"></i> Reintentar Envío
            </Button>
          )}
          <Button variant="danger" onClick={() => handleDeleteOne(selectedEmail.id)}>
            <i className="bi bi-trash me-1"></i> Eliminar
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailQueue;
